import { useCallback, useEffect, useRef, useState } from "react";
import type { Authorization, Mailing, MailingsStatus, MailingSummary } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog } from "../../ui/Dialog";
import type { MailScheme } from "../orders/mail-dom";
import { MailPreview } from "../orders/MailPreview";

interface Props {
  /** The Beheer code: whoever may save the actions may send a mailing, as the mail about the Dagpromo. */
  authorization: Authorization;
}

const SCHEMES: readonly MailScheme[] = ["light", "dark"];
const EMPTY: Mailing = {
  subject: "",
  label: "",
  title: "",
  text: "",
  highlight: "",
  infoTitle: "",
  infoText: "",
  orderUrl: "",
  pizza: false,
};
/** What the owner typed stays on this device until it is sent: a mailing is often written in two goes. */
const DRAFT_KEY = "tafo.mailing.draft";
/** The logo is shown 34 px high: four times that is sharp on every phone, and stays small. */
const LOGO_HEIGHT = 136;
const LOGO_MAX_WIDTH = 800;

function loadDraft(): Mailing | null {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    return saved === null ? null : { ...EMPTY, ...(JSON.parse(saved) as Partial<Mailing>) };
  } catch {
    return null;
  }
}

function saveDraft(mailing: Mailing | null): void {
  try {
    if (mailing === null) localStorage.removeItem(DRAFT_KEY);
    else localStorage.setItem(DRAFT_KEY, JSON.stringify(mailing));
  } catch {
    // no storage (a private window): the draft only lives as long as the screen
  }
}

/** The chosen picture made small as a PNG in base64, so the mail stays light and transparency stays. */
async function smallLogo(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, LOGO_HEIGHT / bitmap.height, LOGO_MAX_WIDTH / bitmap.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/png").replace(/^data:[^,]*,/, "");
}

/** "do 22/10 00:01" */
function when(iso: string): string {
  return new Intl.DateTimeFormat("nl-BE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** The value of a datetime-local field for a moment: "2026-10-22T00:01" in the device's time. */
function localValue(at: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

function stateOf(mailing: MailingSummary): string {
  const text = t.promotions.mailing;
  if (mailing.cancelledAt !== null) return text.stateCancelled(mailing.sent);
  if (mailing.planned && mailing.waiting > 0) return text.statePlanned;
  return text.stateSent(mailing.sent, mailing.recipients);
}

/**
 * Rondzending (Furkan, 21/10/2026), under the actions in Beheer › Acties: the shop writes its own mail
 * ("Vandaag al open om 11:00") to its customers who want its actions by mail, looks at it, sends itself a test,
 * and sends it now or at a chosen time; a planned one can be cancelled. Each customer gets his own link to
 * stop these mails; mails about orders and the stamp card do not depend on it. The test goes to the shop's own
 * address at Tafo, never to one typed here; one mailing a day (Furkan, 30/10/2026: "4a prima, b oké").
 */
export function MailingPanel({ authorization }: Props) {
  const { api, report, toast } = useTill();
  const text = t.promotions.mailing;
  const staffToken = authorization.token;
  const [status, setStatus] = useState<MailingsStatus | null>(null);
  const [mailing, setMailing] = useState<Mailing>(() => loadDraft() ?? EMPTY);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [scheme, setScheme] = useState<MailScheme>(() =>
    document.documentElement.dataset.theme === "dark" ? "dark" : "light",
  );
  const [testing, setTesting] = useState(false);
  /** The send window: now or later, and when it opened (a later time must lie after that). */
  const [sending, setSending] = useState<{ later: boolean; at: string; opened: number } | null>(null);
  const [asking, setAsking] = useState(false);
  const [cancelling, setCancelling] = useState<MailingSummary | null>(null);
  const logoFile = useRef<HTMLInputElement>(null);

  const reload = useCallback(
    (signal?: AbortSignal) =>
      api
        .request<MailingsStatus>("GET", "/device/mailings", { retry: true, ...(signal ? { signal } : {}) })
        .then((answer) => {
          setStatus(answer);
          // the folder's link to start from, once
          setMailing((before) =>
            before.orderUrl === "" && loadDraft() === null ? { ...before, orderUrl: answer.orderUrl } : before,
          );
        })
        .catch((error: unknown) => {
          if (signal?.aborted !== true) report(error);
        }),
    [api, report],
  );

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  const change = (part: Partial<Mailing>) => {
    setMessage(null);
    setMailing((before) => {
      const next = { ...before, ...part };
      saveDraft(next);
      return next;
    });
  };

  /** Ready to show or send: a subject, a title and a link that is one. */
  const ready = (): boolean => {
    if (mailing.subject.trim() === "" || mailing.title.trim() === "") {
      setMessage(text.missing);
      return false;
    }
    if (mailing.orderUrl.trim() !== "" && !/^https:\/\/[^\s"'<>]+$/.test(mailing.orderUrl.trim())) {
      setMessage(text.orderUrlWrong);
      return false;
    }
    return true;
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  const showPreview = () => {
    if (!ready()) return;
    void run(async () => {
      setPreview(
        await api.request<{ subject: string; html: string }>("POST", "/device/mailings/preview", { body: { mailing } }),
      );
    });
  };

  const sendTest = () =>
    run(async () => {
      const answer = await api.request<{ to: string; skipped: boolean }>("POST", "/device/mailings/test", {
        staffToken,
        body: { mailing },
      });
      // the test shop mails only addresses of tafo.be: one it kept unsent is never shown as sent
      if (answer.skipped) toast(text.testSkipped(answer.to), "bad");
      else toast(text.testSent(answer.to));
      setTesting(false);
    });

  const send = () =>
    run(async () => {
      const at = sending?.later === true ? new Date(sending.at) : null;
      const answer = await api.request<{ recipients: number; sendAt: string }>("POST", "/device/mailings", {
        staffToken,
        body: { mailing, sendAt: at === null ? null : at.toISOString() },
      });
      toast(at === null ? text.sent(answer.recipients) : text.planned(answer.recipients, when(answer.sendAt)));
      setSending(null);
      // a fresh form, with the shop's link for "Bestel online" again
      setMailing({ ...EMPTY, orderUrl: status?.orderUrl ?? "" });
      saveDraft(null);
      await reload();
    });

  const cancel = (target: MailingSummary) =>
    run(async () => {
      const answer = await api.request<{ cancelled: number }>("DELETE", `/device/mailings/${target.id}`, {
        staffToken,
      });
      toast(text.cancelled(answer.cancelled));
      await reload();
    });

  const uploadLogo = (file: File | undefined) => {
    if (file === undefined) return;
    void run(async () => {
      setStatus(
        await api.request<MailingsStatus>("PUT", "/device/mailings/logo", {
          staffToken,
          body: { data: await smallLogo(file) },
        }),
      );
      toast(text.logoSaved);
    });
  };

  const removeLogo = () =>
    run(async () => {
      setStatus(await api.request<MailingsStatus>("DELETE", "/device/mailings/logo", { staffToken }));
    });

  const field = (
    key: "subject" | "label" | "highlight" | "infoTitle" | "orderUrl",
    label: string,
    max: number,
    hint?: string,
  ) => (
    <label className="field">
      <span>
        {label} {hint !== undefined && <span className="note">{hint}</span>}
      </span>
      <input
        value={mailing[key]}
        maxLength={max}
        inputMode={key === "orderUrl" ? "url" : undefined}
        onChange={(event) => {
          change({ [key]: event.target.value });
        }}
      />
    </label>
  );

  const area = (key: "title" | "text" | "infoText", label: string, max: number, rows: number) => (
    <label className="field">
      <span>{label}</span>
      <textarea
        value={mailing[key]}
        maxLength={max}
        rows={rows}
        onChange={(event) => {
          change({ [key]: event.target.value });
        }}
      />
    </label>
  );

  const recipients = status?.recipients ?? 0;
  const testAddress = status?.testAddress ?? "";
  const laterWrong =
    sending?.later === true &&
    (sending.at === "" ||
      new Date(sending.at).getTime() < sending.opened ||
      new Date(sending.at).getTime() > sending.opened + 14 * 24 * 60 * 60 * 1000);

  return (
    <div className="panel admin-section mailing">
      <h2>{text.title}</h2>
      <p className="note">{status === null ? "" : recipients === 0 ? text.none : text.note(recipients)}</p>

      <div className="mailing-form">
        {field("subject", text.subject, 120, text.subjectHint)}
        <div className="pegrid">
          {field("label", text.label, 30, text.labelHint)}
          {field("highlight", text.highlight, 60, text.highlightHint)}
        </div>
        {area("title", text.heading, 80, 2)}
        {area("text", text.text, 600, 4)}
        <div className="pegrid">
          {field("infoTitle", text.infoTitle, 60)}
          {field("orderUrl", text.orderUrl, 300, text.orderUrlHint)}
        </div>
        {area("infoText", text.infoText, 300, 2)}
        <label className="mailing-check">
          <input
            type="checkbox"
            checked={mailing.pizza}
            onChange={(event) => {
              change({ pizza: event.target.checked });
            }}
          />
          {text.pizza}
        </label>
        <p className="note">{text.callNote}</p>

        <div className="mailing-logo">
          <span>{text.logo}:</span>
          {status?.logoUrl ? <img src={status.logoUrl} alt="" /> : <span className="note">{text.logoNone}</span>}
          <input
            ref={logoFile}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            aria-label={text.logo}
            onChange={(event) => {
              uploadLogo(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <button type="button" className="btn small" disabled={busy} onClick={() => logoFile.current?.click()}>
            {status?.logoUrl ? text.logoReplace : text.logoChoose}
          </button>
          {status?.logoUrl && (
            <button type="button" className="btn small danger ghost" disabled={busy} onClick={() => void removeLogo()}>
              {text.logoRemove}
            </button>
          )}
        </div>
      </div>

      {message !== null && <p className="pinmsg">{message}</p>}
      <div className="mailing-actions">
        <button type="button" className="btn" disabled={busy} onClick={showPreview}>
          {text.preview}
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || status === null}
          onClick={() => {
            if (ready()) setTesting(true);
          }}
        >
          {text.test}
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={busy || recipients === 0}
          onClick={() => {
            if (!ready()) return;
            const opened = Date.now();
            setSending({ later: false, at: localValue(new Date(opened + 60 * 60 * 1000)), opened });
          }}
        >
          {text.send}
        </button>
      </div>

      {status !== null && status.mailings.length > 0 && (
        <div className="mailing-history">
          <h3>{text.history}</h3>
          <ul>
            {status.mailings.map((item) => (
              <li key={item.id}>
                <span className="mailing-subject">{item.subject}</span>
                <span className="note">
                  {when(item.sendAt)} · {stateOf(item)}
                </span>
                {item.cancelledAt === null && item.waiting > 0 && (
                  <button
                    type="button"
                    className="btn small danger ghost"
                    disabled={busy}
                    onClick={() => {
                      setCancelling(item);
                    }}
                  >
                    {text.cancel}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview !== null && (
        <Dialog
          title={text.previewTitle}
          subtitle={preview.subject}
          onClose={() => {
            setPreview(null);
          }}
          actions={
            <div className="seg" role="group" aria-label={t.orders.mails.version}>
              {SCHEMES.map((option) => (
                <button
                  type="button"
                  key={option}
                  aria-pressed={scheme === option}
                  onClick={() => {
                    setScheme(option);
                  }}
                >
                  {t.orders.mails.versions[option]}
                </button>
              ))}
            </div>
          }
        >
          <MailPreview html={preview.html} scheme={scheme} />
        </Dialog>
      )}

      {testing && (
        <Dialog
          title={text.testTitle}
          size="narrow"
          onClose={() => {
            setTesting(false);
          }}
          footer={
            <button
              type="button"
              className="btn primary"
              disabled={busy || testAddress === ""}
              onClick={() => void sendTest()}
            >
              {text.testSend}
            </button>
          }
        >
          {testAddress === "" ? (
            <p className="mailing-test-to">{text.testNoAddress}</p>
          ) : (
            <>
              <p className="mailing-test-to">
                {text.testTo} <strong>{testAddress}</strong>
              </p>
              <p className="note">{text.testToNote}</p>
            </>
          )}
        </Dialog>
      )}

      {sending !== null && (
        <Dialog
          title={text.sendTitle}
          subtitle={mailing.subject}
          size="narrow"
          onClose={() => {
            setSending(null);
          }}
          footer={
            <button
              type="button"
              className="btn primary"
              disabled={busy || laterWrong}
              onClick={() => {
                setAsking(true);
              }}
            >
              {sending.later ? text.plan : text.confirm(recipients)}
            </button>
          }
        >
          <div className="mailing-when" role="radiogroup" aria-label={text.sendTitle}>
            <label>
              <input
                type="radio"
                checked={!sending.later}
                onChange={() => {
                  setSending({ ...sending, later: false });
                }}
              />
              {text.now}
            </label>
            <label>
              <input
                type="radio"
                checked={sending.later}
                onChange={() => {
                  setSending({ ...sending, later: true });
                }}
              />
              {text.later}
            </label>
          </div>
          {sending.later && (
            <label className="field">
              <span>{text.at}</span>
              <input
                type="datetime-local"
                value={sending.at}
                onChange={(event) => {
                  setSending({ ...sending, at: event.target.value });
                }}
              />
            </label>
          )}
          {laterWrong && <p className="pinmsg">{text.atWrong}</p>}
          <p className="note">{text.oncePerDay}</p>
        </Dialog>
      )}

      {asking && sending !== null && (
        <ConfirmDialog
          message={
            sending.later
              ? text.confirmLater(recipients, when(new Date(sending.at).toISOString()))
              : `${text.confirm(recipients)}?`
          }
          confirm={sending.later ? text.plan : text.confirm(recipients)}
          tone="primary"
          onConfirm={() => void send()}
          onClose={() => {
            setAsking(false);
          }}
        />
      )}

      {cancelling !== null && (
        <ConfirmDialog
          message={text.cancelConfirm}
          confirm={text.cancel}
          onConfirm={() => void cancel(cancelling)}
          onClose={() => {
            setCancelling(null);
          }}
        />
      )}
    </div>
  );
}
